/**
 * Sky render pass — analytical Preetham-lite atmosphere drawn as a fullscreen
 * triangle behind the terrain. Reads camera (group 0) and daynight (group 2);
 * group 1 is bound as an empty layout to satisfy WebGPU's contiguous-group rule.
 *
 * Depth: always-pass, no write — the cleared depth buffer (1.0) remains intact
 * for the terrain pass that follows in the same render pass.
 */

import { createRenderPipeline } from '../gpu/pipelines.ts';
import { createEmptyBindGroup } from '../gpu/buffers.ts';
import wgsl from './sky.wgsl?raw';

export interface SkyPass {
  draw(pass: GPURenderPassEncoder): void;
  destroy(): void;
}

export interface SkyPassOptions {
  presentationFormat: GPUTextureFormat;
  depthFormat: GPUTextureFormat;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
}

export function createSkyPass(device: GPUDevice, opts: SkyPassOptions): SkyPass {
  const empty = createEmptyBindGroup(device, 'sky');

  const pipelineLayout = device.createPipelineLayout({
    label: 'sky.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      empty.layout,
      opts.dayNightBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'sky',
    layout: pipelineLayout,
    targets: [{ format: opts.presentationFormat }],
    primitive: { topology: 'triangle-list', cullMode: 'none' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'always',
      depthWriteEnabled: false,
    },
  });

  return {
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, empty.bindGroup);
      pass.draw(3);
    },
    destroy() {
      // Pipeline and bind groups are GC'd with the device; nothing manual to release.
    },
  };
}
